var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _Redactor_instances, _Redactor_redact, _Redactor_redactObject, _Redactor_redactRecursively;
import { isErrorLike } from './utils.js';
// eslint-disable-next-line no-underscore-dangle
export const _REDACTION = '[:REDACTED:]'; // distinguish from redacted string returned by the redact library.
export const DEFAULT_REGEXP = {
    // Pattern to catch SSN. With our without hyphenation.
    // e.g.
    // 123 45 6789
    // 123-45-6789
    ssn: '\\b\\d{3}(-|\\s)\\d{2}(-|\\s)\\d{4}\\b',
    // 16 digit credit card number.
    // 4111 1111 1111 1111
    // 4111-1111-1111-1111
    // 4111111111111111
    '16-credit-card': '\\b\\d{4}(-|\\s)?\\d{4}(-|\\s)?\\d{4}(-|\\s)?\\d{4}\\b',
    // # 15 digit credit card number.
    // 1111-111111-11111
    // 1111 111111 11111
    // 111111111111111
    '15-credit-card': '\\b\\d{4}(-|\\s)?\\d{6}(-|\\s)?\\d{5}\\b',
    // # Telephone number
    // 1231231234
    // 123-123-1234
    // 123 123 1234
    // (123) 123-1234
    // 123.123.1234
    'telephone-number': '\\s?(\\(\\d{3}\\)|\\d{3})(\\s|-|\\.)?\\d{3}(\\s|-|\\.)?\\d{4}\\b',
    // International telephone numbers. Same as `TelephoneDomestic` but with a +xxx prefix.
    // (Needed two regexps because a single RegExp to catch both domestic and international
    // numbers overlaps with the pattern for SSNs).
    // Note that international calling codes vary from 1 to three digits.
    // +1 123 456 789
    'intl-telephone-number': '\\+\\d{1,3}(\\s|-|\\.)?(\\(\\d{3}\\)|\\d{3})?(\\s|-|\\.)?\\d{3}(\\s|-|\\.)?\\d{4}\\b',
    // # Email
    // foo@bar.com
    // foo@bar.tk
    // foo.bar@baz.qux
    // foo.bar+blah@baz.quux
    email: '\\b[^\\s@]+@[^\\s@\\.]+\\.\\w+\\b',
};
export const SENSITIVE_PARAMS = [
    'access_token',
    'account_name',
    'account_number',
    'accountNumber',
    'address',
    'address_line_one',
    'address_line_two',
    'addressLineOne',
    'addressLineTwo',
    'api_key',
    'apikey',
    'authorization',
    'bill_pin',
    'canonical_merchant_name',
    'card_number',
    'cf-access-jwt-assertion',
    'citizenship',
    'city',
    'cobrand_token',
    'cookie',
    'cookies',
    'country',
    'csrf_token',
    'cvc',
    'date_of_birth',
    'dateOfBirth',
    'description',
    'description_original',
    'description_simple',
    'email',
    'expiration_month',
    'expiration_year',
    'financial_account_id',
    'first_name',
    'firstName',
    'full_name',
    'full_ssn',
    'fullSsn',
    'homeAddressLine1',
    'homeAddressLine2',
    'jwt_access_token',
    'last_4',
    'last_four',
    'last_four_ssn',
    'last_name',
    'last4',
    'last4SSN',
    'lastFour',
    'lastName',
    'latitude',
    'longitude',
    'merchant_name',
    'merchant_name_ds',
    'middle_initial',
    'middle_name',
    'name_on_account',
    'nw_session_token',
    'offer',
    'online_bank_url',
    'passwd',
    'password',
    'phone',
    'phoneNumber',
    'phone_number',
    'phpsessid',
    'postal_code',
    'postalCode',
    'region',
    'routing_number',
    'routingNumber',
    'secret',
    'sentry_dsn',
    'social security number',
    'social_security_number',
    'socialSecurityNumber',
    'ssn',
    'state',
    'street',
    'street_address',
    'streetAddress',
    'streetAddress2',
    'street2',
    'token',
    'uid',
    'user_token',
    'visitor_token',
    'zip',
    'zipcode',
].map((param) => param.toLowerCase());
export function GetDefaultRules() {
    const regexpRules = Object.entries(DEFAULT_REGEXP).map(([name, regexp]) => {
        return {
            name,
            apply: (input, replacement) => {
                if (typeof input === 'string' && new RegExp(regexp).test(input)) {
                    return input.replace(new RegExp(regexp, 'g'), replacement);
                }
                return input;
            },
        };
    });
    const propertyRule = {
        name: 'property_name',
        apply: (input, replacement, objectKey) => {
            const inputType = typeof input;
            if (!input || ['boolean', 'number'].includes(inputType)) {
                return input;
            }
            const matchingParam = SENSITIVE_PARAMS.indexOf(typeof objectKey === 'string' ? objectKey?.toLowerCase() : objectKey);
            if (matchingParam === -1) {
                return input;
            }
            return replacement;
        },
    };
    const includePropertyRule = {
        name: 'include_property',
        apply: (input, replacement, objectKey) => {
            const inputType = typeof input;
            if (!input || ['boolean', 'number'].includes(inputType)) {
                return input;
            }
            const matchingParam = SENSITIVE_PARAMS.find((param) => typeof objectKey === 'string' &&
                objectKey?.toLowerCase()?.includes(param));
            if (!matchingParam) {
                return input;
            }
            return replacement;
        },
    };
    const rules = [
        ...regexpRules,
        propertyRule,
        includePropertyRule,
        {
            name: 'request',
            apply: (input, _, objectKey) => {
                if (objectKey !== 'request') {
                    return input;
                }
                if (input === null) {
                    return input;
                }
                if (typeof input !== 'object') {
                    return input;
                }
                // instead of just plucking headers we want
                // we just redact those that fall into the default rules
                const tmpHeaders = input.headers || {};
                const headers = {};
                // eslint-disable-next-line no-restricted-syntax
                for (const key in tmpHeaders) {
                    if (typeof tmpHeaders[key] !== 'string') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }
                    headers[key] = [...regexpRules, propertyRule].reduce((acc, rule) => {
                        return rule.apply(acc, _REDACTION, key);
                    }, [tmpHeaders[key]]);
                }
                return {
                    uri: input.uri,
                    method: input.method,
                    headers,
                };
            },
        },
    ];
    return {
        name: 'default',
        rules,
    };
}
class Redactor {
    constructor({ rules, replacement }) {
        _Redactor_instances.add(this);
        this.replacement = _REDACTION;
        this.rules = rules;
        this.replacement = replacement;
    }
    apply(input) {
        return __classPrivateFieldGet(this, _Redactor_instances, "m", _Redactor_redactRecursively).call(this, input, new Set());
    }
}
_Redactor_instances = new WeakSet(), _Redactor_redact = function _Redactor_redact(input, key) {
    const { rules } = this.rules ?? {};
    const result = rules?.reduce((acc, rule) => {
        return rule.apply(acc, this.replacement, key);
    }, input);
    return result;
}, _Redactor_redactObject = function _Redactor_redactObject(input, seen, key) {
    // Avoid circular references.
    if (seen.has(input)) {
        return '--circular-reference-omitted--';
    }
    seen.add(input);
    if (Array.isArray(input)) {
        return input.map((item) => __classPrivateFieldGet(this, _Redactor_instances, "m", _Redactor_redactRecursively).call(this, item, seen, key));
    }
    const newObj = {};
    if (isErrorLike(input)) {
        const newError = new Error();
        const properties = Object.getOwnPropertyNames(input);
        properties.forEach((propertyName) => {
            newError[propertyName] = __classPrivateFieldGet(this, _Redactor_instances, "m", _Redactor_redactRecursively).call(this, input[propertyName], seen, propertyName);
        });
        return newError;
    }
    // - Remember, "in" iterates through properties of an object (and "of" through arrays).
    // - Anything that doesn't show up as an enumerable property (hello symbols and other exotica) will simply be omitted.
    // - We use for...in  instead of Object.keys because we want to go up the prototype chain and grab everything that we can. Object.keys
    //   only gives you the object's own enumerable propeties.
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const property in input) {
        newObj[property] = __classPrivateFieldGet(this, _Redactor_instances, "m", _Redactor_redactRecursively).call(this, input[property], seen, property);
    }
    return newObj;
}, _Redactor_redactRecursively = function _Redactor_redactRecursively(input, seen, key) {
    const inputType = typeof input;
    // we don't need to redact numbers, boolean, undefined or null
    if (!input || inputType === 'boolean' || inputType === 'number') {
        return input;
    }
    if (inputType === 'string') {
        return __classPrivateFieldGet(this, _Redactor_instances, "m", _Redactor_redact).call(this, input, key);
    }
    if (inputType === 'object') {
        return __classPrivateFieldGet(this, _Redactor_instances, "m", _Redactor_redactObject).call(this, input, seen, key);
    }
    // No applicable rules for this value -> nothing to redact. Return as is.
    return input;
};
export { Redactor as _Redactor };
// eslint-disable-next-line no-underscore-dangle
export function _applyDisabledRules(rulesGroup, disabledRules) {
    let skip = false;
    const newRulesArr = disabledRules.reduce((acc, disabledRule) => {
        if (skip) {
            return acc;
        }
        if (typeof disabledRule === 'string' && disabledRule === rulesGroup.name) {
            skip = true;
            return [];
        }
        return acc.filter((rule) => !disabledRule[rulesGroup.name]?.includes(rule.name));
    }, rulesGroup.rules);
    return {
        name: rulesGroup.name,
        rules: newRulesArr,
    };
}
export function createRedactor({ customRules = [], replacement = _REDACTION, disabledRules = [], }) {
    const defaultRules = GetDefaultRules();
    // Disable is only applied to the default rules
    // If the customRule should be disabled, it shouldn't be passed
    // into the redactor
    const newRules = _applyDisabledRules(defaultRules, disabledRules);
    const mergedRules = newRules.rules.concat(customRules);
    return new Redactor({
        rules: {
            name: newRules.name,
            rules: mergedRules.filter((value, idx, arr) => arr.indexOf(value) === idx),
        },
        replacement,
    });
}
export default createRedactor;
//# sourceMappingURL=index.js.map